package de.connect2x.timmy.customcomponents

import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.SmartToy
import androidx.compose.material3.Checkbox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.unit.dp
import de.connect2x.timmy.GlobalCustomConfig
import de.connect2x.timmy.i18n.I18nViewWcImpl
import de.connect2x.trixnity.messenger.MatrixMessengerConfiguration
import de.connect2x.trixnity.messenger.compose.view.DI
import de.connect2x.trixnity.messenger.compose.view.Platform
import de.connect2x.trixnity.messenger.compose.view.PlatformType
import de.connect2x.trixnity.messenger.compose.view.buttonPointerModifier
import de.connect2x.trixnity.messenger.compose.view.common.Tooltip
import de.connect2x.trixnity.messenger.compose.view.files.SaveFileDialog
import de.connect2x.trixnity.messenger.compose.view.get
import de.connect2x.trixnity.messenger.compose.view.i18n.I18nView
import de.connect2x.trixnity.messenger.compose.view.isMobile
import de.connect2x.trixnity.messenger.compose.view.pointerMoveFilter
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.details.ElementDetailsViewSelector
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.message.FileBasedRoomMessageTimelineElementView
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.message.LoadingProgresses
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.message.TextRoomMessageTimelineElementView
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.message.bubble.FileContentOverlay
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.message.bubble.MessageBubble
import de.connect2x.trixnity.messenger.compose.view.room.timeline.element.util.shortenFileName
import de.connect2x.trixnity.messenger.compose.view.theme.components
import de.connect2x.trixnity.messenger.compose.view.theme.components.ModalDialogContent
import de.connect2x.trixnity.messenger.compose.view.theme.components.ModalDialogFooter
import de.connect2x.trixnity.messenger.compose.view.theme.components.ModalDialogHeader
import de.connect2x.trixnity.messenger.compose.view.theme.components.ThemedButton
import de.connect2x.trixnity.messenger.compose.view.theme.components.ThemedDropdownMenuItem
import de.connect2x.trixnity.messenger.compose.view.theme.components.ThemedModalDialog
import de.connect2x.trixnity.messenger.compose.view.util.ifNotNull
import de.connect2x.trixnity.messenger.extensions.tim.compose.i18n.I18nTIMView
import de.connect2x.trixnity.messenger.extensions.tim.downloads.TIMFileBasedRoomMessageTimelineElementViewModel
import de.connect2x.trixnity.messenger.viewmodel.room.timeline.elements.BaseTimelineElementHolderViewModel
import de.connect2x.trixnity.messenger.viewmodel.room.timeline.elements.message.RoomMessageTimelineElementViewModel

/**
 * Diese Implementierung ersetzt TIMFileBasedRoomMessageTimelineElementViewImpl aus den trixnity-messenger-tim-extensions.
 * Einige der hier übernommenen Klassen stammen aus dem implementierten Interface FileBasedRoomMessageTimelineElementView.
 * Wir überschreiben die Klasse, damit die Dateivorschau deaktiviert werden kann. Alle für FdS angepassten Stellen sind
 * mit einem Kommentar dokumentiert.
 */
class FileBasedRoomMessageTimelineElementViewWcImpl : FileBasedRoomMessageTimelineElementView {
    @Composable
    override fun create(
        holder: BaseTimelineElementHolderViewModel,
        element: RoomMessageTimelineElementViewModel.FileBased<*>,
        isPreview: Boolean,
        displayProgressOverElement: Boolean,
        index: Int,
        overlay: (@Composable BoxScope.() -> Unit)?,
        content: @Composable ColumnScope.(showActionMenu: () -> Unit, onSave: () -> Unit) -> Unit,
    ) {
        val error = element.downloadMediaError.collectAsState().value
        var saveDialogOpen by remember { mutableStateOf(false) }
        if (saveDialogOpen) SaveFileDialog(
            element.name,
            element.mimeType,
            error,
            element::downloadMedia,
        ) { saveDialogOpen = false }
        if (element is TIMFileBasedRoomMessageTimelineElementViewModel) {
            ShowTimWarningsOnDownloads(element) {
                element.cancelDownloadWarning()
                saveDialogOpen = false
            }
        }
        FileBasedRoomMessageTimelineElementMessageBubble(
            holder,
            element,
            { saveDialogOpen = true },
            isPreview,
            displayProgressOverElement,
            index,
            overlay,
            content
        )
    }

    @Composable
    fun FileBasedRoomMessageTimelineElementMessageBubble(
        holder: BaseTimelineElementHolderViewModel,
        element: RoomMessageTimelineElementViewModel.FileBased<*>,
        onSave: () -> Unit,
        isPreview: Boolean = false,
        displayProgressOverElement: Boolean,
        index: Int,
        overlay: (@Composable BoxScope.() -> Unit)? = null,
        content: @Composable ColumnScope.(() -> Unit, () -> Unit) -> Unit
    ) {
        val i18n = DI.get<I18nView>()
        val configuration = DI.get<MatrixMessengerConfiguration>()

        MessageBubble(
            holder,
            needsMaxWidth = true,
            additionalContextActions = { onClose ->
                // name
                Tooltip(
                    { Text("${element.name}${element.size.ifNotNull { " $it" }}") } // full name
                ) {
                    Text(
                        "${shortenFileName(element)}${element.size.ifNotNull { " $it" }}", // shortened name
                        modifier = Modifier.padding(5.dp),
                        maxLines = 1,
                    )
                }
                HorizontalDivider()
                // download action
                BaseTimelineElementHolderContextMenuAction(
                    label = i18n.downloadMessage(),
                    isEnabled = !configuration.downloadsDisabled,
                    action = onSave,
                ).render(onClose)
            },
            isPreview = isPreview,
            isMentioned = false,
            index = index,
        ) { showActionMenu ->
            Column {
                // Diese Stelle haben wir für FdS angepasst, um die Medienvorschau zu deaktivieren
                if (GlobalCustomConfig.filePreviewDisabled) {
                    // Übernommen aus TextBasedRoomMessageTimelineElementView
                    when (Platform.current) {
                        PlatformType.DESKTOP, PlatformType.WEB -> SelectionContainer {
                            MessageTextContent(holder, element)
                        }

                        PlatformType.ANDROID, PlatformType.IOS -> MessageTextContent(holder, element)
                    }
                } else {
                    FileBasedView(
                        holder,
                        element,
                        onSave,
                        showActionMenu,
                        displayProgressOverElement,
                        isPreview,
                        overlay,
                        content
                    )
                }

                if (element.hasCaption) {
                    TextRoomMessageTimelineElementView(holder, element, showActionMenu)
                }
            }
        }
    }
}

@Composable
fun FileBasedRoomMessageTimelineElement(
    holder: BaseTimelineElementHolderViewModel,
    element: RoomMessageTimelineElementViewModel.FileBased<*>,
    isPreview: Boolean = false,
    displayProgressOverElement: Boolean = false,
    index: Int,
    overlay: @Composable BoxScope.() -> Unit,
    content: @Composable ColumnScope.(showActionMenu: () -> Unit, onSave: () -> Unit) -> Unit,
) {
    // Diesen Teil haben wir für FdS angepasst, um direkt auf unsere eigene Implementierung zuzugreifen
    FileBasedRoomMessageTimelineElementViewWcImpl()
        .create(holder, element, isPreview, displayProgressOverElement, index, overlay, content)
}

@Composable
internal fun FileBasedView(
    holder: BaseTimelineElementHolderViewModel,
    element: RoomMessageTimelineElementViewModel.FileBased<*>,
    onSave: () -> Unit,
    showActionMenu: () -> Unit,
    displayProgressOverElement: Boolean,
    isPreview: Boolean,
    overlay: (@Composable BoxScope.() -> Unit)? = null,
    content: @Composable ColumnScope.(onShowActionMenu: () -> Unit, openElementDetails: () -> Unit) -> Unit
) {
    val hoverMessage = remember { mutableStateOf(false) }

    val elementDetailsFactory = DI.get<ElementDetailsViewSelector>().rememberFactory(element)
    var openElementDetails by remember { mutableStateOf(false) }
    Box {
        Column(
            Modifier
                .pointerInput(Unit) {
                    detectTapGestures(
                        onTap = {
                            openElementDetails = true
                        },
                        onLongPress = { showActionMenu() },
                    )
                }
                .buttonPointerModifier()
                .then(
                    if (isPreview) Modifier
                    else Modifier.pointerMoveFilter(
                        onEnter = {
                            hoverMessage.value = true
                            true
                        }, onExit = {
                            hoverMessage.value = false
                            true
                        })
                )
        ) {
            Box(modifier = Modifier.width(IntrinsicSize.Min)) {
                Column {
                    // content based on the actual file
                    content(showActionMenu) {
                        openElementDetails = true
                    }
                }
                if (!isPreview) {
                    FileContentOverlay(
                        hoverMessage,
                        overlay,
                    )
                }
            }
            if (!displayProgressOverElement) {
                LoadingProgresses(holder, element, Modifier.align(Alignment.CenterHorizontally))
            }
        }
        if (displayProgressOverElement) {
            LoadingProgresses(holder, element, Modifier.align(Alignment.Center))
        }

    }
    if (openElementDetails && elementDetailsFactory != null) {
        elementDetailsFactory.create(element, onSave, onClose = { openElementDetails = false })
    }
}

// aus TextBasedRoomMessageTimelineElementView
@Composable
private fun MessageTextContent(
    holder: BaseTimelineElementHolderViewModel,
    element: RoomMessageTimelineElementViewModel<*>,
) {
    val i18n = DI.get<I18nViewWcImpl>()

    Column(Modifier.padding(start = 10.dp, end = 10.dp, top = 10.dp)) {
        if (element is RoomMessageTimelineElementViewModel.TextBased.Notice) {
            Row {
                Icon(Icons.Filled.SmartToy, i18n.automated())
                Text(i18n.automated(), fontStyle = FontStyle.Italic)
            }

            Spacer(Modifier.size(5.dp))
        }

        if (element is RoomMessageTimelineElementViewModel.TextBased.Emote) {
            Text("${holder.sender.collectAsState().value?.name}", fontStyle = FontStyle.Italic)
            Spacer(Modifier.size(5.dp))
        }

        // Hier haben wir den Code für FdS angepasst, um eine kurze Erklärung anzuzeigen, wenn die Dateivorschau
        // deaktiviert ist.
        val text = i18n.filePreviewDeactivated()
        Text(text,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.primary,
            fontStyle = FontStyle.Italic)
    }
}

// aus contextMenuActions.kt
class BaseTimelineElementHolderContextMenuAction(
    val label: String,
    val isEnabled: Boolean = true,
    internal val action: () -> Unit,
) {
    operator fun invoke() = action()

    @Composable
    internal fun render(onClose: () -> Unit) {
        when {
            Platform.current.isMobile -> bottomSheetItem(onClose)
            else -> dropDownMenuItem(onClose)
        }
    }

    @Composable
    internal fun dropDownMenuItem(
        onClose: () -> Unit,
    ) {
        val i18n = DI.get<I18nView>()
        Tooltip(
            enabled = !isEnabled,
            tooltip = { Text(i18n.commonButtonDisabled()) },
        ) {
            ThemedDropdownMenuItem(
                enabled = isEnabled,
                text = { Text(label) },
                onClick = {
                    onClose()
                    action()
                },
            )
        }
    }

    @Composable
    internal fun bottomSheetItem(
        onClose: () -> Unit,
    ) {
        val i18n = DI.get<I18nView>()
        Tooltip(
            enabled = !isEnabled,
            tooltip = { Text(i18n.commonButtonDisabled()) }
        ) {
            Text(
                label,
                color = if (isEnabled)
                    MaterialTheme.colorScheme.onBackground
                else
                    MaterialTheme.colorScheme.onBackground.copy(alpha = 0.38f),
                modifier = Modifier
                    .padding(20.dp)
                    .fillMaxWidth()
                    .clickable {
                        action()
                        onClose()
                    },
            )
        }
    }
}

// Aus TIMFileBasedRoomMessageTimelineElementViewImpl (trixnity-messenger-tim-extensions)
@Composable
private fun ShowTimWarningsOnDownloads(
    timFileBasedMessageViewModel: TIMFileBasedRoomMessageTimelineElementViewModel,
    onDismiss: () -> Unit,
) {
    val i18n = DI.current.get<I18nTIMView>()
    val i18nOrig = DI.current.get<I18nView>()
    val showDownloadWarning = timFileBasedMessageViewModel.showDownloadWarning.collectAsState().value
    var disableDownloadWarning by remember { mutableStateOf(false) }

    if (showDownloadWarning) {
        ThemedModalDialog(
            onDismissRequest = onDismiss,
        ) {
            ModalDialogHeader {
                Text(i18n.timDangerousDownloadWarningTitle())
            }
            ModalDialogContent {
                Text(i18n.timDownloadWarningMessage())
            }
            ModalDialogFooter {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.clickable {
                        disableDownloadWarning = disableDownloadWarning.not()
                    }) {
                    Checkbox(
                        checked = disableDownloadWarning,
                        onCheckedChange = {
                            disableDownloadWarning = disableDownloadWarning.not()
                        },
                    )
                    Text(i18n.timDownloadWarningDisable())
                }
                ThemedButton(
                    style = MaterialTheme.components.commonButton,
                    onClick = onDismiss,
                ) {
                    Text(i18nOrig.actionCancel())
                }
                ThemedButton(
                    style = MaterialTheme.components.primaryButton,
                    onClick = {
                        timFileBasedMessageViewModel.acceptDownloadWarning(disableDownloadWarning)
                    }
                ) {
                    Text(i18n.roomEnableEncryptionWarningConfirmation())
                }
            }
        }
    }
}
